import { ButtonType, ButtonWidget, InlineAlertType, InlineAlertWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { HelpService } from '../../commonInfra/services/HelpService';
import {
    getSessionManagementServiceInstance, SessionManagementService
} from '../../sessionManagement/SessionManagementService';
import { cssClass, fillStyle } from '../../style';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { Set } from '../../ts/models/Set';
import { DownloadActions } from '../../ts/services/download/DownloadActions';
import { ChangeFlowsTagsAction } from '../actions/ChangeFlowsTagsAction';
import { FlowOutputStepInfo } from '../models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo } from '../models/RunFlowTaskInfo';
import { FlowOutputStepRuns } from '../resources/FlowOutputStepRuns';
import { Flows } from '../resources/Flows';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { FlowSubplaceProps } from '../types/FlowSubplaceProps';
import { FlowDescription } from './FlowDescription';
import { FlowOverviewDataGridView } from './FlowOverviewDataGridView';
import { FlowOverviewFlowGraphImage } from './FlowOverviewFlowGraphImage';
import { FlowTags } from './FlowTags';

const cssPrefix = 'FlowOverviewSubplace';

const rootClass = cssClass(cssPrefix, 'root', fillStyle, {
  overflow: 'auto'
});

const downloadButtonClass = cssClass(cssPrefix, 'downloadButton', {
  paddingRight: '10px',
  marginTop: '15px',
  marginBottom: '5px',
  marginRight: '-2px'
});

const flowIncompatibilityWarning = cssClass(cssPrefix, 'incompatibiityWarning', {
  paddingTop: '8px'
});

const contentClass = cssClass(cssPrefix, 'content', {
  padding: `${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.LargeSpace}px`
});

export function flowHasIncompatibleDocumentVersion(subplaceProps: FlowSubplaceProps): boolean {
  // (mechen) flow with incompatible doc version will have their file type as NA, since we cant open/read it.
  return subplaceProps.flowDetailInfo.flowFileType && subplaceProps.flowDetailInfo.flowFileType === 'na';
}

export type FlowOverviewSubplaceState = {
  readonly outputStepInfos: ReadonlyArray<FlowOutputStepInfo>;
  readonly outputStepRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>;
  readonly runFlowTasks: ReadonlyArray<RunFlowTaskInfo>;
};

export class FlowOverviewSubplace extends React.Component<FlowSubplaceProps, FlowOverviewSubplaceState> {


  private sessionManagementService: SessionManagementService;
  private unmounted: boolean = false;

  constructor(props: FlowSubplaceProps) {
    super(props);
    this.sessionManagementService = getSessionManagementServiceInstance();

    this.state = {
      outputStepRuns: null,
      outputStepInfos: null,
      runFlowTasks: null
    };
    this.fetchData();
  }

  private fetchData = () => {
    if (!this.unmounted) {
      this.fetchOutputMostRecentRuns();
      this.fetchOutputStepInfo();
      this.fetchRunFlowTasks();
    }
  };

  private fetchOutputMostRecentRuns = () => {
    if (!this.unmounted) {
      FlowOutputStepRuns.getMostRecentForFlow(this.props.flowDetailInfo.id, false).then((runs) => {
        this.setState({outputStepRuns: runs});
      });
    }
  };

  private fetchOutputStepInfo = () => {
    if (!this.unmounted) {
      Flows.getOutputStepInfos(this.props.flowDetailInfo.id).then((outputStepInfos) => {
        this.setState({outputStepInfos: outputStepInfos});
      });
    }
  };

  private fetchRunFlowTasks = () => {
    if (!this.unmounted) {
      const runFlowTasks = RunFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(this.props.flowDetailInfo.id));
      SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
        this.setState({runFlowTasks: tasks.items});
      });
    }
  };

  private refreshData = () => {
    if (!this.unmounted) {
      this.props.refreshData();
    }
  };

  private editTags = () => {
    const singleton = Set.singletonForIdItem(this.props.flowDetailInfo);
    ChangeFlowsTagsAction.execute(singleton).then(() => this.refreshData());
  };

  private renderDescription = (): JSX.Element => {
    return this.props.flowAllowedActions.setDescription || this.props.flowDetailInfo.description ? (
      <div className={contentClass}>
        <FlowDescription
          flowDetailInfo={this.props.flowDetailInfo}
          editDescriptionAllowed={this.props.flowAllowedActions.setDescription}
          refreshData={this.refreshData}
          sanitizer={this.props.sanitizer}
        />
      </div>
    ) : null;
  };

  private renderTags = (): JSX.Element => {
    return this.props.flowAllowedActions.setTags || this.props.flowDetailInfo.tags.length ? (
      <div className={contentClass}>
        <FlowTags
          editTagAllowed={this.props.flowAllowedActions.setTags}
          editTags={this.editTags}
          siteUrls={this.props.siteUrls}
          tags={this.props.flowDetailInfo.tags}
        />
      </div>
    ) : null;
  };

  public componentDidUpdate(prevProps: FlowSubplaceProps) {
    if (prevProps.flowDetailInfo.id !== this.props.flowDetailInfo.id) {
      this.fetchData();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private renderDownloadButton(): JSX.Element {
    return (
      <div className={downloadButtonClass}>
        <ButtonWidget
          children={Messages.download()}
          buttonType={ButtonType.Outline}
          handleClick={() => DownloadActions.downloadFlow({id: this.props.flowDetailInfo.id})}
          tb-test-id={'action-download-incompatible-flow'}
          testId={'action-download-incompatible-flow'}/>
      </div>
    );
  }

  private renderIncompatibleDocumentVersionBanner(): JSX.Element {
    return (
      <div className={flowIncompatibilityWarning}>
        <InlineAlertWidget
          testId={'incompatible-flow-alert'}
          type={InlineAlertType.Error} >
          <div>
            {Messages.flowOverviewIncompatibleDocumentVersionWarningLine1({SERVER_VERSION: HelpService.externalVersion()})}
          </div>
          <div>
            {Messages.flowOverviewIncompatibleDocumentVersionWarningLine2() + ' '}
          </div>
          {this.props.flowAllowedActions.download ? this.renderDownloadButton() : null}
        </InlineAlertWidget>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div className={rootClass}>
        { this.renderDescription() }
        { flowHasIncompatibleDocumentVersion(this.props) ?
          this.renderIncompatibleDocumentVersionBanner() :
          this.state.outputStepInfos && this.state.runFlowTasks  && this.state.outputStepRuns ?
          <FlowOverviewDataGridView
            flowInfo={this.props.flowDetailInfo}
            updateData={this.fetchData}
            siteUrls={this.props.siteUrls}
            outputStepInfos={this.state.outputStepInfos}
            outputStepRuns={this.state.outputStepRuns}
            runFlowTasks={this.state.runFlowTasks}
            flowAllowedActions={this.props.flowAllowedActions}
            isServerOrSiteAdmin={this.sessionManagementService.isSessionSiteOrServerAdmin()}
            schedulingFlowsEnabled={this.sessionManagementService.schedulingFlowsEnabled()}
            areParametersEnabled={this.sessionManagementService.areParametersEnabled()}
          /> : null
        }
        <FlowOverviewFlowGraphImage flowGraphImageUrl={this.props.flowDetailInfo.flowGraphImageUrl}/>
        { this.renderTags() }
      </div>
    );
  }
}
